import { useEffect, useMemo, useRef, useState } from 'react';

type UsePixExpiracaoParams = {
  dataExpiracao?: string | null;
  tempoTotalSegundos?: number;
  onExpirar?: () => void;
};

type UsePixExpiracaoRetorno = {
  tempoRestanteSegundos: number;
  progresso: number;
  restantePercentual: number;
  expirado: boolean;
  formatado: string;
};

const getSegundosRestantes = (dataExpiracao?: string | null): number => {
  if (!dataExpiracao) return 0;

  const ms = new Date(dataExpiracao).getTime();

  if (Number.isNaN(ms)) return 0;

  return Math.max(0, Math.floor((ms - Date.now()) / 1000));
};

const formatarTempo = (totalSegundos: number): string => {
  const horas = Math.floor(totalSegundos / 3600);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  const segundos = totalSegundos % 60;

  const mm = String(minutos).padStart(2, '0');
  const ss = String(segundos).padStart(2, '0');

  return horas > 0 ? `${String(horas).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
};

const usePixExpiracao = ({
  dataExpiracao,
  tempoTotalSegundos,
  onExpirar
}: UsePixExpiracaoParams): UsePixExpiracaoRetorno => {
  const [tempoRestanteSegundos, setTempoRestanteSegundos] = useState(() =>
    getSegundosRestantes(dataExpiracao)
  );
  const onExpirarRef = useRef(onExpirar);

  useEffect(() => {
    onExpirarRef.current = onExpirar;
  }, [onExpirar]);

  useEffect(() => {
    setTempoRestanteSegundos(getSegundosRestantes(dataExpiracao));

    if (!dataExpiracao) return;

    const id = window.setInterval(() => {
      setTempoRestanteSegundos(getSegundosRestantes(dataExpiracao));
    }, 1000);

    return () => {
      window.clearInterval(id);
    };
  }, [dataExpiracao]);

  const expirado = Boolean(dataExpiracao) && tempoRestanteSegundos <= 0;

  useEffect(() => {
    if (expirado) {
      onExpirarRef.current?.();
    }
  }, [expirado]);

  const tempoTotal = tempoTotalSegundos && tempoTotalSegundos > 0 ? tempoTotalSegundos : 0;

  const progresso = useMemo(() => {
    if (tempoTotal <= 0) return 0;

    const decorrido = Math.max(0, tempoTotal - tempoRestanteSegundos);

    return Math.min(100, Math.round((decorrido / tempoTotal) * 100));
  }, [tempoTotal, tempoRestanteSegundos]);

  return {
    tempoRestanteSegundos,
    progresso,
    restantePercentual: Math.max(0, 100 - progresso),
    expirado,
    formatado: formatarTempo(tempoRestanteSegundos)
  };
};

export default usePixExpiracao;
